import React, { memo, useCallback, useEffect, useMemo } from 'react';

import { Box, Button, Typography } from '@mui/material';
import FormGenerator from '@sharedCR/common/formGenerator';
import _ from 'lodash';
import PropTypes from 'prop-types';

import useForm from 'hooks/useForm';
import { getConfigInfo } from 'utils/loanManagement';

import {
  formContainerStyles,
  previousAddressFormControlsStyles as formActionsStyles,
} from '../../../../../AgentAndSeller/AgentAndSeller.styles';
import { AppraisalOrderContactsCardTestIds } from '../../AppraisalOrderContactsCard.testIds';

import { getManualContactFormConfig } from './ManualContactForm.config';
import { ManualContactFormButtonText } from './ManualContactForm.constants';
import { formWrapperStyles } from './ManualContactForm.styles';
import {
  getManualContactReachabilityErrors,
  getManualContactFormHint,
} from './ManualContactForm.utils';

const defaultPendingChanges = {};

const ManualContactForm = ({
  role,
  fields,
  initialValues,
  isEditingSavedContact = false,
  isLoading = false,
  onCancel,
  onSubmit,
}) => {
  const { config, initialFormValues, schema, fieldsConfig, observableFieldsNames } = useMemo(() => {
    const config = getManualContactFormConfig(fields);

    return {
      config,
      ...getConfigInfo({ applicationData: initialValues, config, isNestedForm: true }),
    };
  }, [fields, initialValues]);

  const {
    form,
    handleChange,
    validationErrors,
    setSchema,
    setInForm,
    validateField,
    validateForm,
    staticValidateField,
    setValidationErrors,
  } = useForm(initialFormValues, fieldsConfig, observableFieldsNames);

  const onFormSubmit = useCallback(
    async e => {
      e.preventDefault();

      if (!(await validateForm({ noFormChangedValidate: true }))) {
        return;
      }

      const reachabilityErrors = getManualContactReachabilityErrors({ values: form, fields });

      if (!_.isEmpty(reachabilityErrors)) {
        setValidationErrors(prevErrors => ({ ...prevErrors, ...reachabilityErrors }));

        return;
      }

      onSubmit(form);
    },
    [fields, form, onSubmit, setValidationErrors, validateForm],
  );

  useEffect(() => {
    setSchema(schema);
  }, [setSchema, schema]);

  return (
    <Box
      noValidate
      component="form"
      data-testid={AppraisalOrderContactsCardTestIds.MANUAL_CONTACT_FORM}
      sx={formWrapperStyles}
      onSubmit={onFormSubmit}
    >
      <Box sx={formContainerStyles}>
        <Typography color="text.secondary" component="p" variant="regular12">
          {getManualContactFormHint(role)}
        </Typography>

        <FormGenerator
          disabledPendingChangesHighlight
          isNestedForm
          config={config}
          form={form}
          handleChange={handleChange}
          isLoading={isLoading}
          numberOfColumns={3}
          pendingChanges={defaultPendingChanges}
          setInForm={setInForm}
          staticValidateField={staticValidateField}
          validateField={validateField}
          validationErrors={validationErrors}
        />

        <Box sx={formActionsStyles}>
          <Button
            data-testid={AppraisalOrderContactsCardTestIds.MANUAL_CONTACT_CANCEL_BUTTON}
            size="small"
            variant="line"
            onClick={onCancel}
          >
            {ManualContactFormButtonText.CANCEL}
          </Button>
          <Button
            data-testid={AppraisalOrderContactsCardTestIds.MANUAL_CONTACT_SUBMIT_BUTTON}
            disabled={isLoading}
            size="small"
            type="submit"
          >
            {isEditingSavedContact
              ? ManualContactFormButtonText.SAVE
              : ManualContactFormButtonText.ADD}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

ManualContactForm.propTypes = {
  role: PropTypes.string.isRequired,
  fields: PropTypes.arrayOf(PropTypes.string).isRequired,
  initialValues: PropTypes.object.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isEditingSavedContact: PropTypes.bool,
  isLoading: PropTypes.bool,
};

export default memo(ManualContactForm);
